import type { AuditEvent } from '@stayafrica/contracts';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  Container,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  NoPermissionState,
  PageHeader,
  Select,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  UserCell,
} from '@stayafrica/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { History } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/features/auth/session';
import { auditQueryOptions, type AuditFilters } from '@/features/audit/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';

export const Route = createFileRoute('/_app/admin/audit')({ component: AuditPage });

const ACTIONS = [
  'auth.login',
  'auth.login_failed',
  'user.invite',
  'user.update',
  'user.deactivate',
  'role.create',
  'role.update',
  'tenant.update',
  'property.update',
  'session.revoke',
  'mfa.enable',
  'password.reset',
];

const ENTITY_TYPES = ['USER', 'ROLE', 'TENANT', 'PROPERTY', 'SESSION'];

function AuditPage() {
  const { t, i18n } = useTranslation(['audit', 'common', 'errors']);
  const canRead = useCan('audit.read');

  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState<AuditEvent | null>(null);

  const filters: AuditFilters = {
    ...(action ? { action } : {}),
    ...(entityType ? { entityType } : {}),
    ...(from ? { from: new Date(`${from}T00:00:00Z`).toISOString() } : {}),
    ...(to ? { to: new Date(`${to}T23:59:59Z`).toISOString() } : {}),
  };

  const query = useInfiniteQuery({ ...auditQueryOptions(filters), enabled: canRead });
  const events = query.data?.pages.flatMap((page) => page.data) ?? [];

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const actorLabel = (event: AuditEvent) =>
    event.actorName ??
    (event.actorType === 'SYSTEM' ? t('audit:actorSystem') : t('audit:actorPlatform'));

  return (
    <Container>
      <PageHeader
        title={t('audit:title')}
        subtitle={t('audit:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('audit:title') },
        ]}
      />

      <Card>
        <CardHeader
          title={t('audit:title')}
          icon={<History className="ico ico-16" aria-hidden="true" />}
        />

        <Toolbar>
          <div style={{ width: 220 }}>
            <Select
              value={action}
              onValueChange={setAction}
              aria-label={t('audit:filters.action')}
              placeholder={t('audit:filters.allActions')}
              options={[
                { value: '', label: t('audit:filters.allActions') },
                ...ACTIONS.map((code) => ({
                  value: code,
                  label: t(`audit:actions.${code}`, { defaultValue: code }),
                })),
              ]}
            />
          </div>
          <div style={{ width: 180 }}>
            <Select
              value={entityType}
              onValueChange={setEntityType}
              aria-label={t('audit:filters.entityType')}
              placeholder={t('audit:filters.allEntities')}
              options={[
                { value: '', label: t('audit:filters.allEntities') },
                ...ENTITY_TYPES.map((type) => ({ value: type, label: type })),
              ]}
            />
          </div>
          <div style={{ width: 160 }}>
            <FormField label={t('audit:filters.from')}>
              {(field) => (
                <Input
                  id={field.id}
                  type="date"
                  value={from}
                  onChange={(event) => setFrom(event.target.value)}
                />
              )}
            </FormField>
          </div>
          <div style={{ width: 160 }}>
            <FormField label={t('audit:filters.to')}>
              {(field) => (
                <Input
                  id={field.id}
                  type="date"
                  value={to}
                  onChange={(event) => setTo(event.target.value)}
                />
              )}
            </FormField>
          </div>
          <span className="spacer" />
          {action || entityType || from || to ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAction('');
                setEntityType('');
                setFrom('');
                setTo('');
              }}
            >
              {t('common:actions.clearFilters')}
            </Button>
          ) : null}
        </Toolbar>

        {query.isPending ? (
          <TableSkeleton rows={6} columns={5} label={t('common:states.loading')} />
        ) : query.isError ? (
          <ErrorState
            title={t('common:states.errorTitle')}
            description={errorMessage(query.error, t)}
            traceId={errorTraceId(query.error)}
            retryLabel={t('common:actions.retry')}
            onRetry={() => void query.refetch()}
            traceCopyLabel={t('common:actions.copy')}
            traceCopiedLabel={t('common:actions.copied')}
          />
        ) : events.length === 0 ? (
          <EmptyState
            icon={<History className="ico ico-24" aria-hidden="true" />}
            title={t('audit:emptyTitle')}
            description={t('audit:emptyBody')}
          />
        ) : (
          <>
            <TableWrap>
              <Table compact>
                <thead>
                  <tr>
                    <Th>{t('audit:columns.occurredAt')}</Th>
                    <Th>{t('audit:columns.actor')}</Th>
                    <Th>{t('audit:columns.action')}</Th>
                    <Th>{t('audit:columns.entity')}</Th>
                    <Th>{t('audit:columns.ip')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <Tr
                      key={event.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelected(event)}
                      tabIndex={0}
                      onKeyDown={(keyEvent) => {
                        if (keyEvent.key === 'Enter') setSelected(event);
                      }}
                    >
                      <Td numeric className="t-13">
                        {formatDateTime(event.occurredAt, i18n.language)}
                      </Td>
                      <Td>
                        <UserCell
                          avatar={<Avatar name={actorLabel(event)} size="sm" />}
                          name={actorLabel(event)}
                        />
                      </Td>
                      <Td>
                        <Badge tone={event.action.endsWith('_failed') ? 'red' : 'violet'} square>
                          {t(`audit:actions.${event.action}`, { defaultValue: event.action })}
                        </Badge>
                      </Td>
                      <Td className="t-13">
                        <span className="strong">{event.entityType}</span>
                        {event.entityReference ? (
                          <span className="cell-user__sub">{event.entityReference}</span>
                        ) : null}
                      </Td>
                      <Td numeric className="t-13">
                        {event.ip ?? '—'}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>

            {query.hasNextPage ? (
              <div className="load-more">
                <Button
                  variant="secondary"
                  loading={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                >
                  {t('common:actions.loadMore')}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Card>

      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        {selected ? (
          <DialogContent
            title={t('audit:detailTitle')}
            description={t(`audit:actions.${selected.action}`, { defaultValue: selected.action })}
            closeLabel={t('common:actions.close')}
            icon={<History className="ico ico-16" aria-hidden="true" />}
            className="audit-detail"
          >
            <div className="stack stack--md">
              <dl className="dl dl--lines dl--left">
                <dt>{t('audit:columns.occurredAt')}</dt>
                <dd>{formatDateTime(selected.occurredAt, i18n.language)}</dd>
                <dt>{t('audit:columns.actor')}</dt>
                <dd>{actorLabel(selected)}</dd>
                <dt>{t('audit:columns.entity')}</dt>
                <dd>
                  {selected.entityType}
                  {selected.entityReference ? ` · ${selected.entityReference}` : ''}
                </dd>
                {selected.reason ? (
                  <>
                    <dt>{t('audit:reason')}</dt>
                    <dd>{selected.reason}</dd>
                  </>
                ) : null}
                {selected.traceId ? (
                  <>
                    <dt>{t('audit:columns.trace')}</dt>
                    <dd className="mono">{selected.traceId}</dd>
                  </>
                ) : null}
              </dl>

              {selected.before || selected.after ? (
                <div className="diff-grid">
                  <div className="diff-pane diff-pane--before">
                    <div className="diff-pane__head">{t('audit:before')}</div>
                    <pre>{JSON.stringify(selected.before ?? {}, null, 2)}</pre>
                  </div>
                  <div className="diff-pane diff-pane--after">
                    <div className="diff-pane__head">{t('audit:after')}</div>
                    <pre>{JSON.stringify(selected.after ?? {}, null, 2)}</pre>
                  </div>
                </div>
              ) : (
                <p className="muted mb-0">{t('audit:noDiff')}</p>
              )}
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </Container>
  );
}
